import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Building2, Users, DollarSign, Link2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SearchableSelect from "@/components/SearchableSelect";
import ListSearchBar, { LoadMore } from "@/components/ListSearchBar";
import usePagedSearch, { useDebounced } from "@/hooks/usePagedSearch";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const STATUSES = ["new", "contacted", "onboarding", "onboarded", "dismissed"];
const STATUS_META = {
  new: { label: "New", color: "bg-slate-100 text-slate-600" },
  contacted: { label: "Contacted", color: "bg-sky-100 text-sky-700" },
  onboarding: { label: "Onboarding", color: "bg-amber-100 text-amber-700" },
  onboarded: { label: "Onboarded", color: "bg-emerald-100 text-emerald-700" },
  dismissed: { label: "Dismissed", color: "bg-slate-100 text-slate-400" },
};

export default function VendorLeads() {
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const q = useDebounced(search);
  // Searched, counted and paged on the server.
  const list = usePagedSearch("/staff/vendor-leads/search", { q, status: filter });
  const leads = list.items || [];
  const load = list.reload;
  const [vendors, setVendors] = useState([]);
  const [onboardTarget, setOnboardTarget] = useState(null);
  const [pickedVendorId, setPickedVendorId] = useState("");

  useEffect(() => { api.get("/staff/vendors").then((r) => setVendors(r.data)); }, []);

  const counts = list.counts || { all: 0 };

  const setStatus = async (lead, status) => {
    try {
      await api.patch(`/staff/vendor-leads/${lead.id}`, { status });
      toast.success(`Moved "${lead.display_name}" to ${STATUS_META[status].label}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const openOnboard = (lead) => { setOnboardTarget(lead); setPickedVendorId(""); };

  const confirmOnboard = async () => {
    if (!pickedVendorId) return toast.error("Choose a vendor company to link");
    try {
      await api.patch(`/staff/vendor-leads/${onboardTarget.id}`, { status: "onboarded", onboarded_vendor_company_id: pickedVendorId });
      toast.success(`"${onboardTarget.display_name}" marked onboarded`);
      setOnboardTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to onboard");
    }
  };

  return (
    <div className="p-6 md:p-8" data-testid="vendor-leads-page">
      <h1 className="text-2xl font-extrabold tracking-tight">Vendor Leads</h1>
      <p className="text-slate-500 text-sm mt-1">Out-of-network vendors contractors have added — pick which ones to pursue and onboard into the network.</p>
      <ListSearchBar value={search} onChange={setSearch} placeholder="Search vendor leads by name…" testId="vendor-leads-search" />

      <div className="mt-6 flex flex-wrap gap-2">
        {[["", `All (${counts.all})`], ...STATUSES.map((s) => [s, `${STATUS_META[s].label} (${counts[s] || 0})`])].map(([value, label]) => (
          <button
            key={value || "all"} onClick={() => setFilter(value)} data-testid={`lead-filter-${value || "all"}`}
            className={`px-3.5 py-1.5 rounded-lg text-[12.5px] font-semibold ${filter === value ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-3">
        {leads.map((lead) => (
          <div key={lead.id} className="border border-slate-200 rounded-xl p-4" data-testid={`vendor-lead-row-${lead.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center font-bold text-[12px] flex-shrink-0"><Building2 size={17} /></div>
                <div>
                  <div className="font-semibold text-[14.5px]">{lead.display_name}</div>
                  <div className="flex items-center gap-3 text-[12px] text-slate-400 mt-0.5">
                    <span className="flex items-center gap-1"><Users size={11} /> {lead.contractor_count} contractor{lead.contractor_count === 1 ? "" : "s"}</span>
                    <span className="flex items-center gap-1"><DollarSign size={11} /> {fmt(lead.total_spend)} logged</span>
                  </div>
                  <div className="text-[12px] text-slate-500 mt-1" data-testid={`lead-emails-${lead.id}`}>
                    {lead.emails && lead.emails.length ? lead.emails.join(" · ") : <span className="text-slate-300">No email on file</span>}
                  </div>
                </div>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${STATUS_META[lead.status].color}`} data-testid={`lead-status-${lead.id}`}>
                {STATUS_META[lead.status].label}
              </span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {lead.status !== "contacted" && lead.status !== "onboarded" && (
                <button onClick={() => setStatus(lead, "contacted")} data-testid={`lead-contacted-${lead.id}`} className="px-3 py-1.5 rounded-lg border border-slate-200 text-[12px] font-semibold hover:bg-slate-50">Mark Contacted</button>
              )}
              {lead.status !== "onboarding" && lead.status !== "onboarded" && (
                <button onClick={() => setStatus(lead, "onboarding")} data-testid={`lead-onboarding-${lead.id}`} className="px-3 py-1.5 rounded-lg border border-slate-200 text-[12px] font-semibold hover:bg-slate-50">Mark Onboarding</button>
              )}
              {lead.status !== "onboarded" && (
                <button onClick={() => openOnboard(lead)} data-testid={`lead-onboard-${lead.id}`} className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-[12px] font-semibold flex items-center gap-1">
                  <Link2 size={12} /> Mark Onboarded
                </button>
              )}
              {lead.status !== "dismissed" && lead.status !== "onboarded" && (
                <button onClick={() => setStatus(lead, "dismissed")} data-testid={`lead-dismiss-${lead.id}`} className="px-3 py-1.5 rounded-lg border border-slate-200 text-[12px] font-semibold text-slate-500 hover:bg-slate-50">Dismiss</button>
              )}
            </div>
          </div>
        ))}
        {list.items && leads.length === 0 && <div className="text-slate-400 text-sm text-center py-10">{q ? "No vendor leads match." : "No vendor leads yet."}</div>}
        <LoadMore items={list.items} nextCursor={list.nextCursor} loadMore={list.loadMore} loadingMore={list.loadingMore} total={counts[filter || "all"]} />
      </div>

      <Dialog open={!!onboardTarget} onOpenChange={(o) => !o && setOnboardTarget(null)}>
        <DialogContent data-testid="onboard-lead-modal">
          <DialogHeader><DialogTitle>Mark "{onboardTarget?.display_name}" Onboarded</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-[12.5px] text-slate-500">Link this lead to the real vendor network record for follow-up.</p>
            <SearchableSelect value={pickedVendorId} onChange={(e) => setPickedVendorId(e.target.value)} data-testid="onboard-vendor-select" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm">
              <option value="">Select vendor company…</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </SearchableSelect>
            <button onClick={confirmOnboard} data-testid="confirm-onboard-button" className="w-full h-10 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-[13px] font-semibold">Confirm</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
